import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Shield, Heart, Users, Activity, Clock, Compass, ShieldCheck, Smile } from 'lucide-react';
import SEO from '../components/SEO';
import SectionHeading from '../components/SectionHeading';
import Button from '../components/Button';
import ServiceCard from '../components/ServiceCard';
import { TestimonialSlider } from '../components/TestimonialCard';
import FAQAccordion from '../components/FAQAccordion';
import CTASection from '../components/CTASection';

import { servicesData } from '../data/servicesData';
import { testimonialsData } from '../data/testimonialsData';
import { faqsData } from '../data/faqsData';

gsap.registerPlugin(ScrollTrigger);

export default function Home({ onOpenConsultation }) {
  const heroRef = useRef(null);
  const heroTitleRef = useRef(null);

  useEffect(() => {
    // Check prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      // Hero entrance animation
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.from(heroTitleRef.current, {
        y: 30,
        opacity: 0,
        duration: 1,
        delay: 0.1
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // Combined FAQs for home page
  const homeFaqs = [
    faqsData[0].questions[0],
    faqsData[0].questions[1],
    faqsData[1].questions[0],
    faqsData[1].questions[1],
    faqsData[2].questions[0],
    faqsData[2].questions[1]
  ];

  return (
    <div className="bg-canvas overflow-hidden">
      <SEO
        title="Home | A Safe Space for Healing & Recovery"
        description="Comprehensive mental healthcare, medically supervised deaddiction, and psychiatric rehabilitation in Miraj, Sangli. Dignified, clinical, and human-centered recovery."
      />

      {/* 1. HERO SECTION */}
      <section ref={heroRef} className="relative w-full overflow-hidden bg-canvas">
        {/* MOBILE & TABLET HERO LAYOUT (block xl:hidden): White Card for Text & Buttons, Image fitted below */}
        <div className="block xl:hidden w-full bg-[#F5FAF9] pt-4 sm:pt-6 pb-0">
          <div className="max-w-4xl mx-auto px-3 sm:px-6">
            <div className="bg-white rounded-2xl p-3.5 sm:p-6 border border-[#D9E8E5] shadow-card space-y-3 sm:space-y-4 overflow-hidden">
              <h1 className="font-serif text-base sm:text-2xl text-[#123B5D] font-normal leading-snug tracking-tight">
                <span className="block">A Safe Space for <span className="text-[#0F766E]">Healing,</span></span>
                <span className="block text-[#0F766E]">Recovery</span>
                <span className="block">& New Beginnings.</span>
              </h1>

              <div className="pt-1 flex flex-row flex-nowrap items-center gap-1.5 sm:gap-3 overflow-hidden">
                <Button
                  onClick={onOpenConsultation}
                  variant="primary"
                  size="xs"
                  showArrow={true}
                  className="shadow-medium sm:text-xs sm:px-3.5 whitespace-nowrap"
                >
                  Book a Consultation
                </Button>

                <Button
                  to="/services"
                  variant="secondary"
                  size="xs"
                  showArrow={true}
                  arrowType="right"
                  className="sm:text-xs sm:px-3.5 whitespace-nowrap"
                >
                  Explore Our Care
                </Button>
              </div>
            </div>
          </div>

          {/* Banner Image fitted cleanly below the white card (Zero white space below) */}
          <div className="mt-3 sm:mt-5 w-full overflow-hidden leading-none">
            <img
              src="/home banner.png"
              alt="UMED Mental Hospital & Psychiatric Rehabilitation"
              className="w-full h-auto object-cover object-right block max-h-[340px] sm:max-h-[460px] md:max-h-[540px]"
            />
          </div>
        </div>

        {/* DESKTOP HERO LAYOUT (hidden xl:block): Overlay Banner for 1280px+ */}
        <div className="hidden xl:block relative w-full">
          <img
            src="/home banner.png"
            alt="UMED Mental Hospital & Psychiatric Rehabilitation"
            className="w-full h-auto block object-contain object-right"
          />

          {/* Left Content Overlay */}
          <div className="absolute inset-0 z-10 flex items-start pt-24">
            <div className="w-full max-w-7xl mx-auto px-8">
              <div ref={heroTitleRef} className="max-w-4xl space-y-4">
                <h1 className="font-serif text-6xl text-[#123B5D] font-normal leading-[1.15] tracking-tight">
                  <span className="block whitespace-nowrap">A Safe Space for <span className="text-[#0F766E]">Healing,</span></span>
                  <span className="block whitespace-nowrap text-[#0F766E]">Recovery</span>
                  <span className="block whitespace-nowrap">& New Beginnings.</span>
                </h1>

                <div className="pt-8 flex flex-row items-center gap-4">
                  <Button
                    onClick={onOpenConsultation}
                    variant="primary"
                    size="md"
                    showArrow={true}
                    className="shadow-medium text-sm py-3 px-6"
                  >
                    Book a Consultation
                  </Button>

                  <Button
                    to="/services"
                    variant="secondary"
                    size="md"
                    showArrow={true}
                    arrowType="right"
                    className="text-sm py-3 px-6"
                  >
                    Explore Our Care
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. TRUST / CREDIBILITY STRIP */}
      <section className="py-12 bg-[#123B5D] text-white border-y border-[#0F766E]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 divide-y md:divide-y-0 md:divide-x divide-white/10">

            <div className="pt-4 md:pt-0 md:px-6 first:px-0">
              <div className="flex items-center gap-2 text-[#2A9D8F] mb-2">
                <Clock className="w-5 h-5" />
                <span className="text-xs uppercase tracking-widest font-semibold">24/7 Care</span>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl text-white font-normal">
                Continuous Psychiatric Supervision
              </h3>
              <p className="text-xs text-[#D9E8E5] mt-2 leading-relaxed">
                Around-the-clock medical oversight, inpatient psychiatric triage, and specialized nursing support.
              </p>
            </div>

            <div className="pt-4 md:pt-0 md:px-6">
              <div className="flex items-center gap-2 text-[#2A9D8F] mb-2">
                <Users className="w-5 h-5" />
                <span className="text-xs uppercase tracking-widest font-semibold">Specialized Team</span>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl text-white font-normal">
                Multidisciplinary Clinical Group
              </h3>
              <p className="text-xs text-[#D9E8E5] mt-2 leading-relaxed">
                Consultant psychiatrists, clinical psychologists, psychiatric social workers, and occupational therapists.
              </p>
            </div>

            <div className="pt-4 md:pt-0 md:px-6">
              <div className="flex items-center gap-2 text-[#2A9D8F] mb-2">
                <Activity className="w-5 h-5" />
                <span className="text-xs uppercase tracking-widest font-semibold">Tailored Plans</span>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl text-white font-normal">
                Individualized Recovery Pathways
              </h3>
              <p className="text-xs text-[#D9E8E5] mt-2 leading-relaxed">
                Personalized pharmacotherapy, psychological therapy, and cognitive rehabilitation tailored to each patient.
              </p>
            </div>

            <div className="pt-4 md:pt-0 md:px-6">
              <div className="flex items-center gap-2 text-[#2A9D8F] mb-2">
                <ShieldCheck className="w-5 h-5" />
                <span className="text-xs uppercase tracking-widest font-semibold">Privacy First</span>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl text-white font-normal">
                Absolute Confidentiality Code
              </h3>
              <p className="text-xs text-[#D9E8E5] mt-2 leading-relaxed">
                Strict adherence to psychiatric medical confidentiality and protected patient dignity protocols.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* 3. ABOUT UMED PREVIEW */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">

            {/* Image Composition */}
            <div className="md:col-span-5 order-2 md:order-1 relative w-full">
              <div className="relative rounded-3xl overflow-hidden shadow-elevated border border-[#D9E8E5] aspect-[4/5]">
                <img
                  src="/umed-building.jpg"
                  alt="UMED Hospital & Rehabilitation Centre Building"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Editorial Story Content */}
            <div className="md:col-span-7 order-1 md:order-2 space-y-6">
              <SectionHeading
                badge="ABOUT UMED HOSPITAL & REHAB"
                title="Care That Sees the Person, Not Just the Condition."
              />

              <p className="text-sm sm:text-base text-[#64748B] leading-relaxed">
                UMED is a mental health, deaddiction, and psychiatric rehabilitation centre focused on compassionate and professional care. We understand that every person's journey is different, which is why our approach combines medical treatment with counselling, psychotherapy, rehabilitation, and family support.
              </p>

              <p className="text-sm sm:text-base text-[#64748B] leading-relaxed">
                Our goal is to create a safe and respectful environment where individuals can receive the right care, regain confidence, and take meaningful steps towards recovery.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
                <div className="p-5 rounded-2xl bg-white border border-[#D9E8E5] shadow-card">
                  <div className="w-8 h-8 rounded-lg bg-[#F5FAF9] flex items-center justify-center text-[#0F766E] mb-3">
                    <Compass className="w-4 h-4" />
                  </div>
                  <h4 className="font-serif text-lg text-[#123B5D] font-normal mb-1.5">
                    Our Approach
                  </h4>
                  <p className="text-xs text-[#64748B] leading-relaxed">
                    We combine professional psychiatric care with counselling, psychotherapy, rehabilitation, and family support to provide holistic care for every individual.
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-white border border-[#D9E8E5] shadow-card">
                  <div className="w-8 h-8 rounded-lg bg-[#F5FAF9] flex items-center justify-center text-[#0F766E] mb-3">
                    <Heart className="w-4 h-4" />
                  </div>
                  <h4 className="font-serif text-lg text-[#123B5D] font-normal mb-1.5">
                    Our Commitment
                  </h4>
                  <p className="text-xs text-[#64748B] leading-relaxed">
                    We are committed to providing safe, respectful, and personalized care while supporting individuals and families throughout the recovery journey.
                  </p>
                </div>
              </div>

              <div className="pt-2">
                <Button to="/about" variant="outline" size="md" showArrow={true}>
                  Read Our Full Story & Philosophy
                </Button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 4. CORE AREAS OF CARE */}
      <section className="py-20 lg:py-28 bg-[#F5FAF9] border-t border-[#D9E8E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <SectionHeading
              badge="CLINICAL SERVICES"
              title="Core Areas of Specialized Care"
              subtitle="Multidisciplinary psychiatric, psychological, and rehabilitation programs tailored for long-term health."
            />
            <Button to="/services" variant="secondary" size="md" showArrow={true}>
              View All Services
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.slice(0, 3).map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onOpenConsultation={onOpenConsultation}
              />
            ))}
          </div>
        </div>
      </section>

      {/* 5. WHY UMED DIFFERENTIATORS */}
      <section className="py-20 lg:py-28 bg-[#123B5D] text-white border-y border-[#0F766E]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="THE UMED DIFFERENCE"
            title="Why Families Choose UMED for Mental Health & Recovery"
            dark={true}
            align="center"
            className="mb-16"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

            <div className="p-8 rounded-2xl bg-[#0D273E]/90 border border-white/10 shadow-card hover:border-[#2A9D8F] transition-all group">
              <div className="w-12 h-12 rounded-xl bg-[#123B5D] text-[#2A9D8F] flex items-center justify-center mb-5 border border-white/10 group-hover:border-[#2A9D8F]">
                <Heart className="w-6 h-6 text-[#2A9D8F]" />
              </div>
              <h3 className="font-serif text-xl text-white font-normal mb-2.5">
                Compassionate & Respectful Care
              </h3>
              <p className="text-sm text-[#D9E8E5] leading-relaxed">
                Every individual is treated with understanding, dignity, patience, and respect throughout their recovery journey.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-[#0D273E]/90 border border-white/10 shadow-card hover:border-[#2A9D8F] transition-all group">
              <div className="w-12 h-12 rounded-xl bg-[#123B5D] text-[#2A9D8F] flex items-center justify-center mb-5 border border-white/10 group-hover:border-[#2A9D8F]">
                <Users className="w-6 h-6 text-[#2A9D8F]" />
              </div>
              <h3 className="font-serif text-xl text-white font-normal mb-2.5">
                Experienced Care Team
              </h3>
              <p className="text-sm text-[#D9E8E5] leading-relaxed">
                Psychiatrists, psychologists, counsellors, therapists, and care professionals work together to support each individual.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-[#0D273E]/90 border border-white/10 shadow-card hover:border-[#2A9D8F] transition-all group">
              <div className="w-12 h-12 rounded-xl bg-[#123B5D] text-[#2A9D8F] flex items-center justify-center mb-5 border border-white/10 group-hover:border-[#2A9D8F]">
                <Activity className="w-6 h-6 text-[#2A9D8F]" />
              </div>
              <h3 className="font-serif text-xl text-white font-normal mb-2.5">
                Personalized Treatment Plans
              </h3>
              <p className="text-sm text-[#D9E8E5] leading-relaxed">
                Treatment is planned according to each person's condition, needs, progress, and recovery goals.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-[#0D273E]/90 border border-white/10 shadow-card hover:border-[#2A9D8F] transition-all group">
              <div className="w-12 h-12 rounded-xl bg-[#123B5D] text-[#2A9D8F] flex items-center justify-center mb-5 border border-white/10 group-hover:border-[#2A9D8F]">
                <Shield className="w-6 h-6 text-[#2A9D8F]" />
              </div>
              <h3 className="font-serif text-xl text-white font-normal mb-2.5">
                Safe & Supportive Environment
              </h3>
              <p className="text-sm text-[#D9E8E5] leading-relaxed">
                A calm, secure, and supportive setting helps individuals feel comfortable while receiving professional care.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-[#0D273E]/90 border border-white/10 shadow-card hover:border-[#2A9D8F] transition-all group">
              <div className="w-12 h-12 rounded-xl bg-[#123B5D] text-[#2A9D8F] flex items-center justify-center mb-5 border border-white/10 group-hover:border-[#2A9D8F]">
                <Smile className="w-6 h-6 text-[#2A9D8F]" />
              </div>
              <h3 className="font-serif text-xl text-white font-normal mb-2.5">
                Family-Centred Care
              </h3>
              <p className="text-sm text-[#D9E8E5] leading-relaxed">
                Families are guided and involved wherever appropriate, helping them better understand treatment and support recovery.
              </p>
            </div>

            <div className="p-8 rounded-2xl bg-[#0D273E]/90 border border-white/10 shadow-card hover:border-[#2A9D8F] transition-all group">
              <div className="w-12 h-12 rounded-xl bg-[#123B5D] text-[#2A9D8F] flex items-center justify-center mb-5 border border-white/10 group-hover:border-[#2A9D8F]">
                <Compass className="w-6 h-6 text-[#2A9D8F]" />
              </div>
              <h3 className="font-serif text-xl text-white font-normal mb-2.5">
                Support Beyond Treatment
              </h3>
              <p className="text-sm text-[#D9E8E5] leading-relaxed">
                Our approach focuses on recovery, rehabilitation, relapse prevention, and building confidence for everyday life.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* 8. RECOVERY & WELLNESS (THERAPEUTIC ACTIVITIES) */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">

            <div className="md:col-span-6 space-y-6">
              <SectionHeading
                badge="HOLISTIC WELLNESS"
                title="Supporting Recovery Beyond Medical Treatment"
                subtitle="Recovery is more than medical treatment. At UMED, supportive activities and rehabilitation therapies can help individuals improve their confidence, focus, emotional wellbeing, daily routine, and overall quality of life."
              />

              <div className="space-y-4 pt-2">
                <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-card flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-[#F5FAF9] text-[#0F766E] font-bold text-xs mt-0.5">
                    01
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-[#123B5D] font-normal">
                      Cognitive & Brain Activities
                    </h4>
                    <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
                      Structured activities designed to support concentration, memory, thinking skills, and everyday functioning as part of the rehabilitation process.
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-card flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-[#F5FAF9] text-[#0F766E] font-bold text-xs mt-0.5">
                    02
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-[#123B5D] font-normal">
                      Art, Music & Creative Activities
                    </h4>
                    <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
                      Creative activities that encourage self-expression, relaxation, confidence, and positive engagement in a supportive environment.
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-white border border-[#D9E8E5] shadow-card flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-[#F5FAF9] text-[#0F766E] font-bold text-xs mt-0.5">
                    03
                  </div>
                  <div>
                    <h4 className="font-serif text-lg text-[#123B5D] font-normal">
                      Yoga & Mindfulness
                    </h4>
                    <p className="text-xs text-[#64748B] mt-1 leading-relaxed">
                      Mindfulness, relaxation, and yoga-based activities that can support emotional wellbeing, stress management, and healthy daily routines.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-6 w-full">
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <div className="rounded-2xl overflow-hidden shadow-card aspect-square">
                  <img
                    src="https://images.unsplash.com/photo-1513364776144-60967b0f800f?auto=format&fit=crop&w=600&q=80"
                    alt="Art Therapy Studio"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-card aspect-square mt-4 lg:mt-6">
                  <img
                    src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?auto=format&fit=crop&w=600&q=80"
                    alt="Yoga and Meditation"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-card aspect-square -mt-4 lg:-mt-6">
                  <img
                    src="https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&w=600&q=80"
                    alt="Outdoor Green Garden Walking"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-card aspect-square">
                  <img
                    src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=600&q=80"
                    alt="Indoor Recreation and Fitness"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 10. DIGNIFIED TESTIMONIALS */}
      <section className="py-20 lg:py-28 bg-[#F5FAF9] border-t border-[#D9E8E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="PATIENT & FAMILY REFLECTIONS"
            title="Stories of Restored Hope & Healing"
            align="center"
            className="mb-12"
          />

          <TestimonialSlider items={testimonialsData} />
        </div>
      </section>

      {/* 11. FAQ ACCORDION SECTION */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="CLARITY & GUIDANCE"
            title="Frequently Asked Questions"
            align="center"
            className="mb-12"
          />

          <FAQAccordion items={homeFaqs} />

          <div className="mt-10 text-center">
            <Button to="/patient-family" variant="outline" size="md" showArrow={true}>
              View Complete Patient & Family Guide
            </Button>
          </div>
        </div>
      </section>

      {/* 12. FINAL CONTACT CTA */}
      <CTASection onOpenConsultation={onOpenConsultation} />
    </div>
  );
}
